import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listMyMonsters,
  setActiveMonster,
  togglePin,
  releaseMonster,
  storeMonster,
  retrieveMonster,
  regenerateMonsterAppearance,
} from "@/lib/game/monster.functions";
import { regenerateEvolvedAppearance } from "@/lib/game/evolution.functions";
import { Button } from "@/components/ui/button";
import { StatBar } from "@/components/game/StatBar";
import { Panel } from "@/components/game/Panel";
import { CreatureCard } from "@/components/game/CreatureCard";
import { Pill, TypeBadge } from "@/components/game/Badges";
import { MicroLabel, PageHeader, ScreenContainer } from "@/components/game/ScreenKit";
import {
  Pin,
  PinOff,
  Archive,
  ArchiveRestore,
  Boxes,
  Check,
  Sparkles,
  Wand2,
  Loader2,
  Trash2,
  BookOpen,
} from "lucide-react";
import { MonsterVitaDialog } from "@/components/game/MonsterVitaDialog";
import { InfoTip } from "@/components/game/InfoTip";
import { useTutorial } from "@/components/game/TutorialProvider";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import {
  useMonsterActivities,
  ACTIVITY_ICONS,
  type MonsterActivity,
} from "@/hooks/use-monster-activities";

import { cn } from "@/lib/utils";
import { CREATURE_TYPES, xpForLevel, type CreatureType } from "@/lib/game/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { tServerError } from "@/lib/i18n/server-error";

export const Route = createFileRoute("/_app/game/stable")({
  component: StablePage,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

type Monster = {
  id: string;
  name: string;
  species: string;
  type: string;
  level: number;
  xp: number;
  hp: number;
  hp_max: number;
  is_active: boolean;
  pinned: boolean;
  pending_ai: boolean;
  image_url: string | null;
  evo_stage: number;
  stored_at: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
};

function StablePage() {
  useTutorial("stable_intro");
  const { t } = useTranslation("game");
  const qc = useQueryClient();
  const fetchList = useServerFn(listMyMonsters);
  const setActive = useServerFn(setActiveMonster);
  const pin = useServerFn(togglePin);
  const release = useServerFn(releaseMonster);
  const store = useServerFn(storeMonster);
  const retrieve = useServerFn(retrieveMonster);
  const regen = useServerFn(regenerateMonsterAppearance);
  const regenEvolved = useServerFn(regenerateEvolvedAppearance);
  const [tab, setTab] = useState<"team" | "storage">("team");
  const [filter, setFilter] = useState<CreatureType | "all">("all");
  const [regenIds, setRegenIds] = useState<Set<string>>(new Set());
  const [releaseTarget, setReleaseTarget] = useState<Monster | null>(null);
  const [releaseConfirm, setReleaseConfirm] = useState("");
  const [vitaId, setVitaId] = useState<string | null>(null);
  const { getActivity } = useMonsterActivities();

  const { data, isLoading } = useQuery({
    queryKey: ["my-monsters"],
    queryFn: () => fetchList(),
    staleTime: 15_000,
  });

  /**
   * Alles, was vom aktiven Xenomon abhängt, nach einer Stall-Aktion neu holen.
   *
   * Vorher fehlten hier die zwei wichtigsten Schlüssel, und genau daraus
   * entstand der gemeldete Fehler „im Kampf steht noch das alte Tier":
   *   - `["battle-v2"]`  — der Kampf-Screen liest sein Monster aus
   *     `["battle-v2", "monster"]`. Ohne Invalidierung kämpfte man optisch
   *     weiter mit dem vorherigen Xenomon.
   *   - `["my-monster"]` — daran hängen die Kopfzeile (GameShell) und
   *     `MonsterLifecycle` (Entfaltungs-Angebot, Bild-Nachholpfad). Auch die
   *     zeigten nach einem Wechsel noch die alte Kreatur.
   *
   * Der ganze `battle-v2`-Baum wird bewusst grob invalidiert: Eine
   * Stall-Aktion ist selten, und Zone/Fortschritt hängen ebenfalls am Konto.
   */
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["my-monsters"] });
    qc.invalidateQueries({ queryKey: ["my-monster"] });
    qc.invalidateQueries({ queryKey: ["battle-v2"] });
    qc.invalidateQueries({ queryKey: ["monster-moves"] });
    qc.invalidateQueries({ queryKey: ["combat-state"] });
  };

  const activeMut = useMutation({
    mutationFn: (id: string) => setActive({ data: { monsterId: id } }),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["equipment-layout"] });
      const n = (r as { unequippedCount?: number })?.unequippedCount ?? 0;
      toast.success(
        n > 0 ? t("stable.switchedActiveWithUnequip", { count: n }) : t("stable.switchedActive"),
      );
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const pinMut = useMutation({
    mutationFn: (id: string) => pin({ data: { monsterId: id } }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const storeMut = useMutation({
    mutationFn: (id: string) => store({ data: { monsterId: id } }),
    onSuccess: () => {
      invalidate();
      toast.success(t("stable.storedToast"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const retrieveMut = useMutation({
    mutationFn: (id: string) => retrieve({ data: { monsterId: id } }),
    onSuccess: () => {
      invalidate();
      toast.success(t("stable.broughtBack"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const releaseMut = useMutation({
    mutationFn: (id: string) => release({ data: { monsterId: id } }),
    onSuccess: () => {
      invalidate();
      setReleaseTarget(null);
      setReleaseConfirm("");
      toast.success(t("stable.released"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  /**
   * Bildlose Xenomons automatisch nachholen — auch die NICHT aktiven.
   *
   * `MonsterLifecycle` tut das nur für das aktive Xenomon. Für alle anderen
   * zeigte der Stall bei `pending_ai` einen ewigen Spinner, und die
   * Schaltfläche zum Neuzeichnen erscheint erst bei `pending_ai === false`:
   * Es gab also keinen Weg heraus (aufgefallen beim Stilwechsel 05.09.2026,
   * Stefan: Buffer Wurm lädt seit über drei Minuten). Der Server lässt den
   * Auto-Pfad genau dann zu, wenn der Marker gesetzt ist UND kein Bild da ist
   * — überschrieben werden kann nichts. Ein Versuch je Xenomon und Besuch;
   * bei Budget-Stopp bleibt der Marker stehen und der nächste Besuch
   * versucht es erneut.
   */
  const autoRegenAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const m of data?.monsters ?? []) {
      if (!m.pending_ai || m.image_url || autoRegenAttempted.current.has(m.id)) continue;
      autoRegenAttempted.current.add(m.id);
      triggerRegen(m.id);
    }
    // triggerRegen ist eine Funktionsdeklaration dieser Komponente (gehoisted).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.monsters]);

  function triggerRegen(id: string, opts?: { evolved?: boolean }) {
    setRegenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    const call = opts?.evolved
      ? regenEvolved({ data: { monsterId: id } })
      : regen({ data: { monsterId: id, manual: true } });
    call
      .then(() => {
        toast.success("Erscheinung generiert! ✨");
        invalidate();
      })
      .catch((e: Error) => {
        toast.error(tServerError(e));
        invalidate();
      })
      .finally(() => {
        setRegenIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
  }

  const team = (data?.monsters ?? []) as Monster[];
  const storage = (data?.storage ?? []) as Monster[];
  const cap = data?.cap ?? 6;
  const list = tab === "team" ? team : storage;
  const shown = filter === "all" ? list : list.filter((m) => m.type === filter);
  const teamFull = team.length >= cap;

  /**
   * Aktivitäts-Text übersetzt lesen. `ACTIVITY_LABELS` (activities.server.ts)
   * ist fest deutsch und gehört dem Server-Pfad, wo kein `t` zur Verfügung
   * steht — dort geht der Wert als `__t__serverErrors.activity_*` durch
   * `tServerError`. Im Client sind es dieselben Schlüssel, in de UND en
   * gepflegt. Vorher stand hier die deutsche Konstante: ein englischer Spieler
   * las „IN BRUTKAMMER" auf der Pille und bekam den deutschen Einschub in der
   * sonst englischen Sperr-Erklärung.
   */
  const activityLabel = (a: MonsterActivity) => t(`serverErrors.activity_${a}`);

  /**
   * Beide Reiter zeigen dieselbe Kartenliste (`shown` folgt bereits dem
   * aktiven Reiter), deshalb wird sie EINMAL gebaut und in beide
   * `TabsContent` gereicht. Radix hängt immer nur den aktiven ein — im DOM
   * steht sie also nie doppelt.
   */
  const content = isLoading ? (
    <Panel contentClassName="flex items-center justify-center gap-2 py-10">
      <Loader2 className="h-4 w-4 animate-spin text-primary" strokeWidth={2.5} />
      <span className="text-sm text-muted-foreground">{t("stable.loading")}</span>
    </Panel>
  ) : shown.length === 0 ? (
    <Panel grid contentClassName="flex flex-col items-center gap-2 py-10 text-center">
      <Boxes className="h-7 w-7 text-muted-foreground" strokeWidth={2.5} />
      <p className="max-w-[42ch] text-sm text-muted-foreground">
        {tab === "storage" ? t("stable.storageEmpty") : t("stable.noMatchType")}
      </p>
    </Panel>
  ) : (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {shown.map((m) => {
        const type = (m.type ?? "neon") as CreatureType;
        const stored = !!m.stored_at;
        const activity = getActivity(m.id);
        const busyReason = activity
          ? t("stable.busyReason", { name: m.name, activity: activityLabel(activity) })
          : null;
        const working = m.pending_ai || regenIds.has(m.id);
        return (
          <CreatureCard
            key={m.id}
            name={m.name}
            type={type}
            level={m.level}
            imageUrl={m.image_url}
            /* Ohne SPD: spd wird im Zonenkampf in keiner Formel gelesen (Stefan, 09.09.2026). CreatureCard setzt die Spalten
               aus stats.length, aus drei Kacheln werden sauber zwei. */
            stats={[
              ["ATK", m.atk],
              ["DEF", m.def],
            ]}
            /* Bewusst OHNE `rarity`: Xenomons führen keine — `monsters` hat
               keine solche Spalte, Raritäten gibt es nur bei Eiern und
               Gegenständen. Die Karte lässt die Pille dann weg und bleibt in
               Ink. */
            className={cn(
              // Eingelagert = ruhend: das Art-Fenster verliert seine Farbe.
              stored && "[&_img]:grayscale",
            )}
          >
            {/* Marken über dem Art-Fenster. Die Karte hat 14 px Innenabstand
                (p-3.5) und ihr Fenster ist quadratisch — dieselbe Box lässt
                sich deshalb exakt nachstellen, ohne `CreatureCard`
                anzufassen. */}
            <div className="pointer-events-none absolute inset-x-3.5 top-3.5 aspect-square">
              {/* Das Aktiv-Abzeichen kommt bewusst NICHT aus `CreatureCard`
                  (dort steht „Aktiv" als deutsches Literal) — der
                  Sprachumschalter hängt in der Spiel-Navigation, ein
                  englischer Spieler bekäme sonst deutschen Text. Position
                  und Optik sind identisch zur Karte. */}
              {m.is_active && (
                <span className="absolute left-2.5 top-2.5 rounded-full border-2 border-ink bg-primary px-2 py-px font-display text-[10px] uppercase tracking-[0.14em] text-ink">
                  {t("stable.activeBadge")}
                </span>
              )}
              <div className="absolute right-2.5 top-2.5 flex flex-col items-end gap-1">
                {stored && (
                  <Pill style={{ background: "var(--secondary)", color: "var(--foreground)" }}>
                    {t("stable.storedBadge")}
                  </Pill>
                )}
                {activity && !stored && activity !== "combat" && (
                  <Pill style={{ background: "var(--accent)", color: "var(--accent-foreground)" }}>
                    <span aria-hidden>{ACTIVITY_ICONS[activity]}</span>
                    {activityLabel(activity)}
                  </Pill>
                )}
              </div>
              {working && (
                <div
                  className="pointer-events-auto absolute bottom-2.5 right-2.5 rounded-full border-2 border-ink bg-card p-1.5 shadow-hard-sm"
                  title={t("stable.regenTitle")}
                >
                  <Loader2 className="h-4 w-4 animate-spin text-primary" strokeWidth={2.5} />
                </div>
              )}
            </div>

            <div className="mt-3 flex items-start justify-between gap-2">
              <MicroLabel className="line-clamp-2 min-w-0 [overflow-wrap:anywhere]">
                {m.species}
              </MicroLabel>
              {m.evo_stage > 0 && (
                <Pill
                  className="shrink-0"
                  style={{ background: "var(--secondary)", color: "var(--foreground)" }}
                >
                  Evo {m.evo_stage}
                </Pill>
              )}
            </div>

            <div className="mt-3 space-y-2">
              <StatBar label="XP" value={m.xp} max={xpForLevel(m.level)} color="xp" />
              {/* Eigene Kreatur: HP grün (`ally`). Rot gehört im Design den
                  Gegnern. */}
              <StatBar label="HP" value={m.hp} max={m.hp_max} color="ally" />
            </div>

            {!stored && !m.image_url && (
              <div className="mt-3 text-center">
                {working ? (
                  <span className="text-[11px] text-muted-foreground">
                    Erscheinungsbild wird geladen…
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => triggerRegen(m.id)}
                    className="w-full"
                  >
                    <Wand2 /> Erscheinung generieren
                  </Button>
                )}
              </div>
            )}
            {!stored && m.image_url && m.pending_ai && m.evo_stage > 0 && !regenIds.has(m.id) && (
              <div className="mt-3 flex flex-col gap-1.5 text-center">
                <span className="text-[11px] text-muted-foreground">
                  Neue Form wurde nicht fertig gezeichnet.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => triggerRegen(m.id, { evolved: true })}
                  className="w-full"
                >
                  <Wand2 /> Aussehen erneut erzeugen
                </Button>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              {stored ? (
                <>
                  <div className="flex gap-2">
                    <InfoTip
                      className="min-w-0 flex-1"
                      text={teamFull ? t("stable.teamFull", { cap }) : t("stable.bringBack")}
                    >
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => retrieveMut.mutate(m.id)}
                        disabled={teamFull || retrieveMut.isPending}
                        className="w-full"
                      >
                        <ArchiveRestore /> {t("stable.bringIntoTeam")}
                      </Button>
                    </InfoTip>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setVitaId(m.id)}
                      title={t("stable.openVita")}
                      aria-label={t("stable.openVita")}
                    >
                      <BookOpen />
                    </Button>
                  </div>
                  {/* `min-h-11` steht hier ausdrücklich: `size="sm"` gibt nur
                      40 px her, und dieser Knopf startet eine unwiderrufliche
                      Löschung — er muss die 44-px-Vorgabe halten. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setReleaseTarget(m);
                      setReleaseConfirm("");
                    }}
                    className="min-h-11 w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                    title={t("stable.release")}
                  >
                    <Trash2 /> {t("stable.freeButton")}
                  </Button>
                </>
              ) : (
                <div className="flex gap-2">
                  {m.is_active ? (
                    /* Die einzige orangene Schaltfläche des Screens: Es gibt
                       genau EINE aktive Kreatur, und sie ist der Weg ins
                       Spiel. */
                    <Button asChild size="sm" className="min-w-0 flex-1">
                      <Link to="/game/battle">
                        <Sparkles /> {t("stable.play")}
                      </Link>
                    </Button>
                  ) : (
                    <InfoTip
                      className="min-w-0 flex-1"
                      text={busyReason ?? t("stable.makeActiveHint")}
                    >
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => activeMut.mutate(m.id)}
                        disabled={activeMut.isPending || !!busyReason}
                        className="w-full"
                      >
                        <Check /> {t("stable.activate")}
                      </Button>
                    </InfoTip>
                  )}
                  {!m.is_active && (
                    <InfoTip text={t("stable.storeHint")}>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => storeMut.mutate(m.id)}
                        disabled={storeMut.isPending}
                        title={t("stable.store")}
                        aria-label={t("stable.store")}
                      >
                        <Archive />
                      </Button>
                    </InfoTip>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => pinMut.mutate(m.id)}
                    title={m.pinned ? t("stable.unpin") : t("stable.pin")}
                    aria-label={m.pinned ? t("stable.unpin") : t("stable.pin")}
                    className={m.pinned ? "text-foreground" : undefined}
                  >
                    {m.pinned ? <Pin className="fill-current" /> : <PinOff />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setVitaId(m.id)}
                    title={t("stable.openVita")}
                    aria-label={t("stable.openVita")}
                  >
                    <BookOpen />
                  </Button>
                </div>
              )}
            </div>
          </CreatureCard>
        );
      })}
    </div>
  );

  return (
    <div className="relative min-h-screen">
      {/* Hierarchie-Ebene 1: Verlauf (body) + Halftone, keine Schatten. */}
      <div aria-hidden className="halftone pointer-events-none absolute inset-0" />

      <ScreenContainer className="relative z-10">
        <PageHeader
          icon={<Boxes className="h-6 w-6 shrink-0 text-primary" strokeWidth={2.5} />}
          title={t("nav.stable")}
          sub={
            <>
              <InfoTip text={t("stable.capacityTip")}>
                <span className="font-extrabold tabular-nums text-foreground">
                  {t("stable.inTeam", { count: team.length, cap })} <span aria-hidden>ⓘ</span>
                </span>
              </InfoTip>
              <span aria-hidden className="px-1.5">
                ·
              </span>
              <span className="tabular-nums">
                {t("stable.inStorage", { count: storage.length })}
              </span>
            </>
          }
        />

        <Tabs
          value={tab}
          // Radix reicht einen rohen String — hier eingeengt statt gecastet.
          onValueChange={(v) => setTab(v === "storage" ? "storage" : "team")}
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <TabsList>
              <TabsTrigger value="team">{t("stable.tabTeam", { count: team.length })}</TabsTrigger>
              <TabsTrigger value="storage">
                {t("stable.tabStorage", { count: storage.length })}
              </TabsTrigger>
            </TabsList>

            {/* Typ-Filter. Die Typfarbe selbst sagt, wonach gefiltert wird —
                deshalb der echte `TypeBadge` statt einer orangenen Pille:
                Typfarben sind semantisch, und hier tragen sie die Bedeutung. */}
            <div className="flex flex-wrap items-center gap-1.5">
              <Button
                size="sm"
                variant={filter === "all" ? "secondary" : "outline"}
                aria-pressed={filter === "all"}
                onClick={() => setFilter("all")}
                className="min-h-11 sm:min-h-9"
              >
                {t("stable.filterAll")}
              </Button>
              {CREATURE_TYPES.map((tp) => (
                <button
                  key={tp}
                  type="button"
                  aria-pressed={filter === tp}
                  onClick={() => setFilter(tp)}
                  className={cn(
                    "inline-flex min-h-11 cursor-pointer items-center rounded-full border-2 px-1.5 transition sm:min-h-9",
                    filter === tp
                      ? "border-ink bg-secondary shadow-hard-sm"
                      : "border-transparent opacity-55 hover:opacity-100",
                  )}
                >
                  <TypeBadge type={tp} />
                </button>
              ))}
            </div>
          </div>

          {tab === "storage" && (
            <p className="mt-3.5 max-w-[56ch] text-[13px] text-muted-foreground">
              {t("stable.storageHelper")}
            </p>
          )}

          <TabsContent value="team" className="mt-3.5">
            {content}
          </TabsContent>
          <TabsContent value="storage" className="mt-3.5">
            {content}
          </TabsContent>
        </Tabs>
      </ScreenContainer>

      <AlertDialog
        open={!!releaseTarget}
        onOpenChange={(open) => {
          if (!open) {
            setReleaseTarget(null);
            setReleaseConfirm("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("stable.releaseDialog.title", { name: releaseTarget?.name ?? "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("stable.releaseDialog.description", { name: releaseTarget?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={releaseConfirm}
            onChange={(e) => setReleaseConfirm(e.target.value)}
            placeholder={releaseTarget?.name ?? ""}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel>{t("stable.releaseDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={
                !releaseTarget ||
                releaseConfirm.trim() !== releaseTarget.name ||
                releaseMut.isPending
              }
              onClick={() => {
                if (releaseTarget) releaseMut.mutate(releaseTarget.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("stable.releaseDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MonsterVitaDialog
        monsterId={vitaId}
        open={!!vitaId}
        onOpenChange={(o) => {
          if (!o) setVitaId(null);
        }}
      />
    </div>
  );
}
